import { useCallback, useEffect } from "react";

const STORAGE_KEY = "member_form_data";
const EXPIRATION_TIME = 24 * 60 * 60 * 1000; // 24 hours in milliseconds

// Image field names that need special handling
const IMAGE_FIELDS = ["profile_image", "id_image", "letter_of_resignation"];

// Helper function to convert File to base64
const fileToBase64 = file => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () =>
      resolve({
        base64: reader.result,
        name: file.name,
        type: file.type,
        size: file.size
      });
    reader.onerror = error => reject(error);
  });
};

// Helper function to convert base64 back to File
const base64ToFile = (base64Data, filename, mimeType) => {
  const arr = base64Data.split(",");
  const bstr = atob(arr[1]);
  let n = bstr.length;
  const u8arr = new Uint8Array(n);
  while (n--) {
    u8arr[n] = bstr.charCodeAt(n);
  }
  return new File([u8arr], filename, { type: mimeType });
};

// Helper function to convert date strings back to Date objects
const parseDates = data => {
  if (!data) return data;

  // Return primitives as-is (strings, numbers, booleans)
  if (typeof data !== "object" || data === null) {
    return data;
  }

  // Handle arrays
  if (Array.isArray(data)) {
    return data.map(item => parseDates(item));
  }

  const dateFields = [
    "date_of_birth",
    "marital_date",
    "spouse_date_of_birth",
    "date_of_resignation",
    "salvation_date",
    "baptism_date",
    "date_joined_home_cell",
    "date_joined_current_ministry",
    "membership_date"
  ];

  const parsedData = { ...data };

  // Handle nested objects (like in form data)
  Object.keys(parsedData).forEach(key => {
    if (typeof parsedData[key] === "object" && parsedData[key] !== null) {
      if (Array.isArray(parsedData[key])) {
        parsedData[key] = parsedData[key].map(item => parseDates(item));
      } else {
        parsedData[key] = parseDates(parsedData[key]);
      }
    } else if (dateFields.includes(key) && parsedData[key]) {
      parsedData[key] = new Date(parsedData[key]);
    }
  });

  return parsedData;
};

// Helper function to parse images from base64
const parseImages = data => {
  if (!data) return data;

  const parsedData = { ...data };

  IMAGE_FIELDS.forEach(field => {
    if (parsedData[field] && typeof parsedData[field] === "object") {
      if (parsedData[field].base64) {
        try {
          parsedData[field] = base64ToFile(
            parsedData[field].base64,
            parsedData[field].name,
            parsedData[field].type
          );
        } catch (error) {
          console.error(`Error converting ${field} from base64:`, error);
          delete parsedData[field];
        }
      }
    }
  });

  return parsedData;
};

export const useFormPersistence = () => {
  const saveFormData = useCallback(async data => {
    try {
      const existingData = localStorage.getItem(STORAGE_KEY);
      const parsedExistingData = existingData ? JSON.parse(existingData) : {};

      // Handle File objects in the data
      const processedData = { ...data };

      // Convert File objects to base64
      for (const field of IMAGE_FIELDS) {
        if (processedData[field] instanceof File) {
          try {
            processedData[field] = await fileToBase64(processedData[field]);
          } catch (error) {
            console.error(`Error converting ${field} to base64:`, error);
            delete processedData[field];
          }
        }
      }

      // Merge new data with existing data
      const mergedData = {
        ...parsedExistingData,
        ...processedData,
        timestamp: Date.now()
      };

      localStorage.setItem(STORAGE_KEY, JSON.stringify(mergedData));
    } catch (error) {
      console.error("Error saving form data:", error);

      // If quota exceeded, try to save without images
      if (error.name === "QuotaExceededError") {
        console.warn("Storage quota exceeded. Saving data without images.");
        try {
          const dataWithoutImages = { ...data };
          IMAGE_FIELDS.forEach(field => delete dataWithoutImages[field]);

          const existingData = localStorage.getItem(STORAGE_KEY);
          const parsedExistingData = existingData
            ? JSON.parse(existingData)
            : {};

          const mergedData = {
            ...parsedExistingData,
            ...dataWithoutImages,
            timestamp: Date.now()
          };

          localStorage.setItem(STORAGE_KEY, JSON.stringify(mergedData));
        } catch (fallbackError) {
          console.error(
            "Error saving form data without images:",
            fallbackError
          );
        }
      }
    }
  }, []);

  const loadFormData = useCallback(() => {
    try {
      const savedData = localStorage.getItem(STORAGE_KEY);
      if (!savedData) return null;

      const parsedData = JSON.parse(savedData);
      const timestamp = parsedData.timestamp;

      // Check if data has expired
      if (Date.now() - timestamp > EXPIRATION_TIME) {
        localStorage.removeItem(STORAGE_KEY);
        return null;
      }

      // Remove timestamp and parse dates and images in the returned data
      const data = { ...parsedData };
      delete data.timestamp;

      // Parse dates first
      const dataWithDates = parseDates(data);

      // Then parse images
      return parseImages(dataWithDates);
    } catch (error) {
      console.error("Error loading form data:", error);
      return null;
    }
  }, []);

  const clearFormData = useCallback(() => {
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch (error) {
      console.error("Error clearing form data:", error);
    }
  }, []);

  // Clear expired data on mount
  useEffect(() => {
    const savedData = localStorage.getItem(STORAGE_KEY);
    if (savedData) {
      try {
        const parsedData = JSON.parse(savedData);
        const timestamp = parsedData.timestamp;

        if (Date.now() - timestamp > EXPIRATION_TIME) {
          localStorage.removeItem(STORAGE_KEY);
        }
      } catch (error) {
        console.error("Error checking form data expiration:", error);
      }
    }
  }, []);

  return { saveFormData, loadFormData, clearFormData };
};
